import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { partial } from 'underscore';
import { isValidBIC, isValidIBAN, friendlyFormatIBAN, electronicFormatIBAN } from 'ibantools';

import i18n from 'utils/translate';
import { isAustrianIBAN } from 'utils/common';
import RadioButton from 'components/RadioButton';
import RadioButtonGroup from 'components/RadioButtonGroup';
import TextField from 'components/TextField';
import Section from 'components/Section';

import './styles.scss';
import {
  PAYMENT_HOBEX,
  PAYMENT_PAYMENT_SLIP,
  PAYMENT_SEPA,
  PAYMENT_TYPE_ANNUAL,
  PAYMENT_TYPE_BIANNUAL,
  PAYMENT_TYPE_MONTHLY,
  PAYMENT_TYPE_QUARTERLY
} from './constants';

const propTypes = {
  paymentInfo: PropTypes.shape({
    type: PropTypes.oneOf([PAYMENT_HOBEX, PAYMENT_SEPA, PAYMENT_PAYMENT_SLIP]),
    iban: PropTypes.string,
    bic: PropTypes.string,
    paymentOption: PropTypes.oneOf([
      PAYMENT_TYPE_ANNUAL,
      PAYMENT_TYPE_BIANNUAL,
      PAYMENT_TYPE_QUARTERLY,
      PAYMENT_TYPE_MONTHLY
    ]),
    showCreditCard: PropTypes.bool,
    showSepa: PropTypes.bool,
    showPaymentSlip: PropTypes.bool,
    showPaymentOptions: PropTypes.bool
  }),
  submitted: PropTypes.bool,
  onPaymentInfoChange: PropTypes.func,
  onSEPAValidation: PropTypes.func,
  onError: PropTypes.func
};

const defaultProps = {
  paymentInfo: {
    type: PAYMENT_HOBEX
  }
};

class PaymentInformationForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      ibanError: false,
      bicError: false,
      bicRequired: false,
      isBicDirty: props.paymentInfo.bic
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { paymentInfo, submitted, onSEPAValidation } = this.props;

    if (prevProps.submitted !== submitted) {
      if (paymentInfo.type === PAYMENT_SEPA) {
        this.checkValidSepa('iban', paymentInfo.iban);
        this.checkValidSepa('bic', paymentInfo.bic);

        if (!prevState.isBicDirty) {
          this.setState({ isBicDirty: true });
        }
      }
    }

    if (prevProps.paymentInfo !== paymentInfo) {
      // Reset errors if payment type changes, otherwise we can not continue to next step if SEPA error
      if (prevProps.paymentInfo.type === PAYMENT_SEPA && paymentInfo.type !== PAYMENT_SEPA) {
        onSEPAValidation(false);
      }

      if (paymentInfo.type === PAYMENT_SEPA && paymentInfo.iban && !isAustrianIBAN(paymentInfo.iban)) {
        this.setState({ bicRequired: true });
      } else {
        this.setState({ bicRequired: false });
      }
    }
  }

  handlePaymentTypeSelect = (type, e) => {
    if (e.target.checked) {
      const {
        paymentInfo: { iban, bic, showPaymentOptions },
        onPaymentInfoChange
      } = this.props;

      let opt = { type, iban, bic };

      if (showPaymentOptions) {
        if (type === PAYMENT_SEPA) {
          opt.zahlungsweise = PAYMENT_TYPE_ANNUAL;
        } else {
          delete opt.zahlungsweise;
        }
      }
      onPaymentInfoChange && onPaymentInfoChange(opt);
    }
  };

  handlePaymentOptionCheck = async (zahlungsweise, e) => {
    const { paymentInfo, onPaymentInfoChange } = this.props;
    onPaymentInfoChange && onPaymentInfoChange({ ...paymentInfo, zahlungsweise });
  };

  handlePaymentInfoChange = data => {
    if (data.bic && !this.state.isBicDirty) {
      this.setState({ isBicDirty: true });
    }
    const { paymentInfo, onPaymentInfoChange } = this.props;
    onPaymentInfoChange && onPaymentInfoChange({ ...paymentInfo, ...data });
  };

  checkValidSepa = (key, val) => {
    const { bicRequired } = this.state;
    const { onError, onSEPAValidation } = this.props;

    if (key === 'iban') {
      this.setState(
        {
          ibanError: !val ? true : isValidIBAN(electronicFormatIBAN(val)) ? false : true
        },
        () => {
          const { ibanError, bicError } = this.state;
          onSEPAValidation(ibanError || bicError);
          if (onError) {
            setTimeout(onError(ibanError || bicError));
          }
        }
      );
    } else if (key === 'bic') {
      this.setState(
        {
          bicError: bicRequired ? (!val ? true : !isValidBIC(val)) : false
        },
        () => {
          const { ibanError, bicError } = this.state;
          onSEPAValidation(bicError || ibanError);
          if (onError) {
            setTimeout(onError(bicError || ibanError));
          }
        }
      );
    }
  };

  render() {
    let {
      paymentInfo: {
        type,
        iban,
        bic,
        headerText,
        paymentOption,
        showCreditCard = true,
        showSepa = true,
        showPaymentSlip = false,
        showPaymentOptions = false
      }
    } = this.props;
    const { ibanError, bicError, bicRequired, isBicDirty } = this.state;

    if (!showCreditCard && !type) {
      type = PAYMENT_PAYMENT_SLIP;
    }

    if (type === PAYMENT_PAYMENT_SLIP && !paymentOption) {
      paymentOption = PAYMENT_TYPE_ANNUAL;
    }

    return (
      <Section header={headerText ? headerText : i18n('Zahlungsart')}>
        <div className="payment-selection col-xs-12">
          {showCreditCard && (
            <RadioButton
              id="paymentCard"
              text={i18n('Kreditkarte')}
              checked={type === PAYMENT_HOBEX}
              onChange={partial(this.handlePaymentTypeSelect, PAYMENT_HOBEX)}
            />
          )}
          {showSepa && (
            <RadioButton
              id="paymentSEPA"
              text={i18n('SEPA Lastschrift-Mandat')}
              checked={type === PAYMENT_SEPA}
              onChange={partial(this.handlePaymentTypeSelect, PAYMENT_SEPA)}
            />
          )}
          {showPaymentSlip && (
            <RadioButton
              id="erlagschein"
              text={i18n('Erlagschein')}
              checked={type === PAYMENT_PAYMENT_SLIP}
              questionMark={i18n('Der Erlagschein wird der Polizze beigelegt.')}
              onChange={partial(this.handlePaymentTypeSelect, PAYMENT_PAYMENT_SLIP)}
            />
          )}
        </div>
        {type === PAYMENT_HOBEX && (
          <div className="col-xs-12 payment-description">
            {i18n(
              `Sie werden nach Abschluss des vollständig ausgefüllten Formulars durch Klick auf den Button "Online abschließen" zur Bezahlseite weitergeleitet, um die Zahlung per Kreditkarte durchzuführen.`
            )}
          </div>
        )}
        {type === PAYMENT_SEPA && (
          <Fragment>
            {showPaymentOptions && (
              <div className="col-xs-12 payment-type-wrapper">
                <RadioButtonGroup
                  required={true}
                  id="paymentOption"
                  value={paymentOption}
                  onChange={data => this.handlePaymentOptionCheck(data)}
                  options={[
                    { label: i18n('jährlich'), value: PAYMENT_TYPE_ANNUAL },
                    { label: i18n('halbjährlich'), value: PAYMENT_TYPE_BIANNUAL },
                    { label: i18n('vierteljährlich'), value: PAYMENT_TYPE_QUARTERLY },
                    { label: i18n('monatlich'), value: PAYMENT_TYPE_MONTHLY }
                  ]}
                />
              </div>
            )}
            <div className="col-xs-12 payment-description">
              <span>
                <b>{i18n('SEPA Lastschrift-Mandat')}</b>
              </span>
              <p>
                {i18n(`Ich ermächtige / Wir ermächtigen die Grazer Wechselseitige
                Versicherung AG, Zahlungen von meinem / unserem Konto mittels
                Lastschrift einzuziehen. Zugleich weise ich mein / weisen wir
                unser Kreditinstitut an, die von der Grazer Wechselseitigen
                Versicherung AG auf mein / unser Konto gezogenen Lastschriften
                einzulösen. Ich kann / Wir können innerhalb von 8 Wochen,
                beginnend mit dem Belastungsdatum, die Erstattung des belasteten
                Betrages verlangen. Es gelten dabei die mit meinem / unserem
                Kreditinstitut vereinbarten Bedingungen.`)}
              </p>
              <TextField
                label={i18n('IBAN')}
                name="iban"
                required
                value={iban}
                iban
                onChange={data => this.handlePaymentInfoChange({ iban: friendlyFormatIBAN(data) })}
                onBlur={() => {
                  this.checkValidSepa('iban', iban);
                  if (isBicDirty) {
                    this.checkValidSepa('bic', bic);
                  }
                }}
                customError={ibanError ? 'IBAN ungültig' : null}
              />

              {bicRequired && (
                <TextField
                  label={i18n('BIC')}
                  name="bic"
                  required
                  value={bic}
                  onChange={data => this.handlePaymentInfoChange({ bic: data })}
                  onBlur={() => this.checkValidSepa('bic', bic)}
                  customError={bicError ? 'BIC ungültig' : null}
                />
              )}

              <div className="payment-recipient">
                <span>
                  <b>Zahlungsempfänger</b>
                </span>
                <span>Grazer Wechselseitige Versicherung AG</span>
                <span>8010 Graz, Herrengasse 18-20</span>
                <span>Creditor-ID: AT18ZZZ00000002425</span>
              </div>
            </div>
          </Fragment>
        )}
      </Section>
    );
  }
}

PaymentInformationForm.defaultProps = defaultProps;
PaymentInformationForm.propTypes = propTypes;

export default PaymentInformationForm;
